// src/components/staff/mobile/MobileStaffDashboard.jsx
import React, { useState } from 'react';
import {
  Home, ClipboardList, Zap, Fuel, Calendar,
  MessageSquare, AlertTriangle, Users, FileText,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

import MobileStaffHome        from './MobileStaffHome';
import MobileStaffRequests    from './MobileStaffRequests';
import MobileLiveQueue        from './MobileLiveQueue';
import MobileMyTasks          from './MobileMyTasks';
import MobileStaffSchedule    from './MobileStaffSchedule';
import MobileStaffChat        from './MobileStaffChat';
import MobileStaffAlerts      from './MobileStaffAlerts';
import MobileStaffClients     from './MobileStaffClients';
import MobileStaffInvoices    from './MobileStaffInvoices';

const GLOBAL_STYLE = `
  @keyframes spin  { to { transform: rotate(360deg) } }
  @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
  * { -webkit-tap-highlight-color: transparent; }
`;

// ─── Build tab list per level ─────────────────────────────────────────────────
const buildTabs = (level) => {
  // Admin / Manager (90+)
  if (level >= 90) {
    return [
      { id: 'home',     label: 'Home',     Icon: Home          },
      { id: 'requests', label: 'Requests', Icon: ClipboardList },
      { id: 'chat',     label: 'Chat',     Icon: MessageSquare },
      { id: 'alerts',   label: 'Alerts',   Icon: AlertTriangle, isAlert: true },
      { id: 'more',     label: 'More',     Icon: Users         },
    ];
  }
  // Supervisor (75-89)
  if (level >= 75) {
    return [
      { id: 'home',     label: 'Home',     Icon: Home          },
      { id: 'requests', label: 'Requests', Icon: ClipboardList },
      { id: 'schedule', label: 'Schedule', Icon: Calendar      },
      { id: 'chat',     label: 'Chat',     Icon: MessageSquare },
      { id: 'alerts',   label: 'Alerts',   Icon: AlertTriangle, isAlert: true },
    ];
  }
  // Line Crew (30-74)
  if (level >= 30) {
    return [
      { id: 'home',     label: 'Home',     Icon: Home     },
      { id: 'mytasks',  label: 'My Tasks', Icon: Fuel     },
      { id: 'queue',    label: 'Queue',    Icon: Zap      },
      { id: 'schedule', label: 'Schedule', Icon: Calendar },
      { id: 'clients',  label: 'Aircraft', Icon: Users    },
    ];
  }
  // Dispatcher (25-29)
  if (level >= 25) {
    return [
      { id: 'home',     label: 'Home',      Icon: Home          },
      { id: 'queue',    label: 'Live Queue', Icon: Zap          },
      { id: 'schedule', label: 'Schedule',  Icon: Calendar      },
      { id: 'chat',     label: 'Chat',      Icon: MessageSquare },
      { id: 'clients',  label: 'Clients',   Icon: Users         },
    ];
  }
  // Accountant / Cashier (20-24)
  if (level >= 20) {
    return [
      { id: 'home',     label: 'Home',    Icon: Home     },
      { id: 'invoices', label: 'Finance', Icon: FileText },
      { id: 'clients',  label: 'Clients', Icon: Users    },
    ];
  }
  return [{ id: 'home', label: 'Home', Icon: Home }];
};

// ─── More drawer (Admin/Manager overflow) ────────────────────────────────────
const MORE_TABS = [
  { id: 'queue',    label: 'Live Queue', Icon: Zap      },
  { id: 'schedule', label: 'Schedule',  Icon: Calendar  },
  { id: 'clients',  label: 'Clients',   Icon: Users     },
  { id: 'invoices', label: 'Finance',   Icon: FileText  },
];

const MoreDrawer = ({ open, onClose, onSelect }) => {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 150 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'absolute', bottom: 72, left: 16, right: 16,
        background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155',
        padding: '16px', display: 'flex', flexDirection: 'column', gap: 4,
        animation: 'fadeUp 0.22s ease',
      }}>
        <style>{`@keyframes fadeUp { from { opacity:0;transform:translateY(12px) } to { opacity:1;transform:translateY(0) } }`}</style>
        {MORE_TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => { onSelect(id); onClose(); }} style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '13px 14px',
            background: 'none', border: 'none', borderRadius: 14, cursor: 'pointer',
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 11, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon size={18} color="#64748b" />
            </div>
            <span style={{ fontSize: 15, color: '#e2e8f0', fontWeight: 500 }}>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
const TabBar = ({ tabs, active, onChange }) => (
  <nav style={{
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'rgba(8,12,25,0.98)',
    borderTop: '0.5px solid #1e293b',
    display: 'flex',
    paddingBottom: 'env(safe-area-inset-bottom)',
    zIndex: 100,
    backdropFilter: 'blur(20px)',
  }}>
    {tabs.map(({ id, label, Icon, isAlert }) => {
      const on = active === id;
      return (
        <button key={id} onClick={() => onChange(id)} style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 2, padding: '10px 0 8px',
          background: 'none', border: 'none', cursor: 'pointer',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: on
              ? isAlert ? 'rgba(239,68,68,0.18)' : 'rgba(59,130,246,0.18)'
              : 'transparent',
            transition: 'background 0.2s',
          }}>
            <Icon
              size={20}
              color={on ? (isAlert ? '#ef4444' : '#3b82f6') : '#475569'}
              strokeWidth={on ? 2.2 : 1.8}
            />
          </div>
          <span style={{
            fontSize: 9, fontWeight: on ? 600 : 400,
            color: on ? (isAlert ? '#ef4444' : '#3b82f6') : '#475569',
          }}>{label}</span>
        </button>
      );
    })}
  </nav>
);

// ─── Page titles ──────────────────────────────────────────────────────────────
const PAGE_TITLES = {
  requests: 'All Requests',
  queue:    'Live Queue',
  mytasks:  'My Tasks',
  schedule: 'Schedule',
  chat:     'Client Chat',
  alerts:   'Send Alert',
  clients:  'Clients & Aircraft',
  invoices: 'Finance',
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileStaffDashboard = () => {
  const { getUserLevel, hasMinLevel } = useAuth();
  const [tab, setTab]         = useState('home');
  const [showMore, setShowMore] = useState(false);

  const level = getUserLevel();
  const tabs  = buildTabs(level);

  const handleTabChange = (id) => {
    if (id === 'more') { setShowMore(s => !s); return; }
    setShowMore(false);
    setTab(id);
  };

  const renderContent = () => {
    switch (tab) {
      case 'home':     return <MobileStaffHome onNavigate={(t) => { setTab(t); setShowMore(false); }} />;
      case 'requests': return level >= 75  ? <MobileStaffRequests />  : null;
      case 'queue':    return level >= 25  ? <MobileLiveQueue />       : null;
      case 'mytasks':  return level >= 30  ? <MobileMyTasks />         : null;
      case 'schedule': return <MobileStaffSchedule />;
      case 'chat':     return level >= 25  ? <MobileStaffChat />       : null;
      case 'alerts':   return level >= 75  ? <MobileStaffAlerts />     : null;
      case 'clients':  return <MobileStaffClients />;
      case 'invoices': return level >= 20  ? <MobileStaffInvoices />   : null;
      default:         return <MobileStaffHome onNavigate={setTab} />;
    }
  };

  return (
    <div style={{
      minHeight: '100dvh', background: '#0f172a',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
      WebkitFontSmoothing: 'antialiased', color: '#fff',
    }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Safe area top */}
      <div style={{ height: 'env(safe-area-inset-top)' }} />

      {/* Sub-page sticky header */}
      {tab !== 'home' && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'rgba(8,12,25,0.97)',
          borderBottom: '0.5px solid #1e293b',
          padding: '14px 20px',
          backdropFilter: 'blur(20px)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
            {PAGE_TITLES[tab] ?? tab}
          </h1>
          {tab === 'alerts' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'rgba(239,68,68,0.12)', border: '0.5px solid rgba(239,68,68,0.25)', borderRadius: 20 }}>
              <div style={{ width: 6, height: 6, borderRadius: 3, background: '#ef4444', animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontSize: 11, color: '#fca5a5', fontWeight: 600 }}>Live</span>
            </div>
          )}
          {tab === 'chat' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'rgba(34,197,94,0.12)', border: '0.5px solid rgba(34,197,94,0.25)', borderRadius: 20 }}>
              <div style={{ width: 6, height: 6, borderRadius: 3, background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontSize: 11, color: '#86efac', fontWeight: 600 }}>Online</span>
            </div>
          )}
        </div>
      )}

      {tab === 'home' && <div style={{ height: 8 }} />}

      <main>{renderContent()}</main>

      <MoreDrawer open={showMore} onClose={() => setShowMore(false)} onSelect={(id) => { setTab(id); setShowMore(false); }} />

      <TabBar tabs={tabs} active={tab} onChange={handleTabChange} />
    </div>
  );
};

export default MobileStaffDashboard;